import { STATUS_CONFIG, ASSET_TYPE_CONFIG } from "@/lib/constants";
import { X, MapPin, GasPump, Package, User, Gauge, Compass, ArrowsClockwise, Path } from "@phosphor-icons/react";

export default function AssetDetailPanel({ asset, onClose, onToggleTrail, showTrail }) {
  const status = STATUS_CONFIG[asset.status] || STATUS_CONFIG.idle;
  const type = ASSET_TYPE_CONFIG[asset.type] || { label: asset.type };

  const loadPct = asset.capacity_lbs > 0
    ? Math.min(100, Math.round((asset.current_load_lbs / asset.capacity_lbs) * 100))
    : 0;
  const overweight = asset.current_load_lbs > asset.capacity_lbs && asset.capacity_lbs > 0;

  return (
    <div
      className="h-full bg-[#0d0d0d]/95 backdrop-blur-xl border border-[#2a2a2a] flex flex-col overflow-hidden"
      data-testid="asset-detail-panel"
    >
      {/* Header */}
      <div className="border-b border-[#2a2a2a] p-4 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 hover:bg-[#1a1a1a] flex items-center justify-center"
          data-testid="close-asset-panel"
        >
          <X size={16} />
        </button>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">{type.label}</div>
        <h2 className="font-heading font-black text-2xl tracking-tight mt-1" data-testid="asset-name">{asset.name}</h2>

        <div className="flex items-center gap-2 mt-3">
          <span
            className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold"
            style={{ background: status.bg, color: status.color, border: `1px solid ${status.color}` }}
          >
            <span className="w-1.5 h-1.5 rounded-full ll-status-blink" style={{ background: status.color }}></span>
            {status.label}
          </span>
          {asset.license_plate && (
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-mono">
              {asset.license_plate}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Live Telemetry */}
        <div className="p-4 border-b border-[#2a2a2a]">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-3 flex items-center gap-2">
            <ArrowsClockwise size={12} className="ll-status-blink" />
            Live Telemetry
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MetricBlock
              icon={<Gauge size={14} />}
              label="Speed"
              value={`${asset.speed?.toFixed(0) || 0}`}
              unit="MPH"
            />
            <MetricBlock
              icon={<Compass size={14} />}
              label="Heading"
              value={`${asset.heading?.toFixed(0) || 0}°`}
              unit={getHeadingLabel(asset.heading)}
            />
            <MetricBlock
              icon={<MapPin size={14} />}
              label="Latitude"
              value={asset.lat?.toFixed(4) || "—"}
              unit="N"
            />
            <MetricBlock
              icon={<MapPin size={14} />}
              label="Longitude"
              value={asset.lng?.toFixed(4) || "—"}
              unit="W"
            />
          </div>
        </div>

        {/* Load */}
        {asset.capacity_lbs > 0 && (
          <div className="p-4 border-b border-[#2a2a2a]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-3 flex items-center gap-2">
              <Package size={12} />
              Load Status
            </div>
            <div className="flex items-baseline justify-between mb-2">
              <span className="font-heading font-black text-2xl" style={{ color: overweight ? "#ef4444" : "#fff" }}>
                {asset.current_load_lbs.toLocaleString()}
              </span>
              <span className="text-xs font-mono text-[#a1a1aa]">/ {asset.capacity_lbs.toLocaleString()} LBS</span>
            </div>
            <div className="h-2 bg-[#1a1a1a] border border-[#2a2a2a] relative overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${Math.min(100, loadPct)}%`,
                  background: overweight ? "#ef4444" : loadPct > 90 ? "#ff8c00" : "#4682b4",
                }}
              ></div>
            </div>
            <div className="flex justify-between mt-2 text-[10px] font-mono uppercase tracking-wider">
              <span style={{ color: overweight ? "#ef4444" : "#a1a1aa" }}>{loadPct}% CAPACITY</span>
              {overweight && (
                <span className="text-[#ef4444] font-bold ll-status-blink">⚠ OVERWEIGHT</span>
              )}
            </div>
          </div>
        )}

        {/* Fuel */}
        {asset.fuel_level > 0 && (
          <div className="p-4 border-b border-[#2a2a2a]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-3 flex items-center gap-2">
              <GasPump size={12} />
              Fuel Level
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-3 bg-[#1a1a1a] border border-[#2a2a2a]">
                <div
                  className="h-full"
                  style={{
                    width: `${asset.fuel_level}%`,
                    background: asset.fuel_level < 25 ? "#ef4444" : asset.fuel_level < 50 ? "#ff8c00" : "#22c55e",
                  }}
                ></div>
              </div>
              <span className="font-mono font-bold text-sm w-12 text-right">{asset.fuel_level}%</span>
            </div>
          </div>
        )}

        {/* Driver */}
        {asset.driver && (
          <div className="p-4 border-b border-[#2a2a2a]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-2 flex items-center gap-2">
              <User size={12} />
              Operator
            </div>
            <div className="text-sm font-semibold">{asset.driver}</div>
          </div>
        )}

        {/* Notes */}
        {asset.notes && (
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-2">Notes</div>
            <div className="text-sm text-[#a1a1aa] leading-relaxed">{asset.notes}</div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="border-t border-[#2a2a2a] p-3 grid grid-cols-3 gap-2">
        <button
          onClick={onToggleTrail}
          className={`py-2 border text-[10px] uppercase tracking-[0.2em] font-bold transition-colors flex items-center justify-center gap-1 ${
            showTrail
              ? "bg-[#4682b4] border-[#4682b4] text-white"
              : "border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-white"
          }`}
          data-testid="trail-toggle-btn"
        >
          <Path size={12} weight="bold" /> Trail
        </button>
        <button
          className="py-2 border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold transition-colors"
          data-testid="dispatch-asset-btn"
        >
          Assign
        </button>
        <button
          className="py-2 bg-[#ff8c00] text-black hover:bg-[#e67e00] text-[10px] uppercase tracking-[0.2em] font-bold transition-colors"
          data-testid="contact-asset-btn"
        >
          Contact
        </button>
      </div>
    </div>
  );
}

function MetricBlock({ icon, label, value, unit }) {
  return (
    <div className="border border-[#2a2a2a] p-3 bg-[#0a0a0a]">
      <div className="text-[#71717a] mb-1 flex items-center gap-1">{icon}</div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#71717a]">{label}</div>
      <div className="font-mono font-bold text-base mt-1">
        {value} <span className="text-[10px] text-[#71717a]">{unit}</span>
      </div>
    </div>
  );
}

function getHeadingLabel(deg) {
  if (deg == null) return "";
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return dirs[Math.round(deg / 45) % 8];
}
